import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Popover,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ChevronLeft, ChevronRight, PushPin as PushPinIcon } from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, addDays, subDays } from 'date-fns';

export type ViewScale = '1-day' | '3-day' | 'week';
export type TimeResolution = '1-min' | '5-min' | '10-min' | '15-min' | '30-min' | '1-hour';

const TIME_RESOLUTIONS: { value: TimeResolution; label: string; minutes: number }[] = [
  { value: '1-min', label: '1 min', minutes: 1 },
  { value: '5-min', label: '5 min', minutes: 5 },
  { value: '10-min', label: '10 min', minutes: 10 },
  { value: '15-min', label: '15 min', minutes: 15 },
  { value: '30-min', label: '30 min', minutes: 30 },
  { value: '1-hour', label: '1 hour', minutes: 60 }
];

export interface TimesheetViewControlsProps {
  currentDate: Date;
  viewScale: ViewScale;
  onDateChange: (date: Date) => void;
  onViewScaleChange: (scale: ViewScale) => void;
  onJumpToNow: () => void;
  /** Show resolution dropdown (for Calendar); omit for Table view */
  showResolution?: boolean;
  timeResolution?: TimeResolution;
  onTimeResolutionChange?: (resolution: TimeResolution) => void;
}

export function getDateRangeFromScale(currentDate: Date, viewScale: ViewScale): Date[] {
  const start = startOfWeek(currentDate, { weekStartsOn: 1 });
  const end = endOfWeek(currentDate, { weekStartsOn: 1 });
  if (viewScale === '1-day') {
    return [currentDate];
  }
  if (viewScale === '3-day') {
    return [currentDate, addDays(currentDate, 1), addDays(currentDate, 2)];
  }
  return eachDayOfInterval({ start, end });
}

export function getStartEndDateStrings(currentDate: Date, viewScale: ViewScale): { startDate: string; endDate: string } {
  const range = getDateRangeFromScale(currentDate, viewScale);
  if (range.length === 0) {
    const d = format(currentDate, 'yyyy-MM-dd');
    return { startDate: d, endDate: d };
  }
  const startDate = format(range[0], 'yyyy-MM-dd');
  const endDate = format(range[range.length - 1], 'yyyy-MM-dd');
  return { startDate, endDate };
}

export default function TimesheetViewControls({
  currentDate,
  viewScale,
  onDateChange,
  onViewScaleChange,
  onJumpToNow,
  showResolution = false,
  timeResolution = '15-min',
  onTimeResolutionChange
}: TimesheetViewControlsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pickerOpen, setPickerOpen] = useState(false);
  const dateButtonRef = useRef<HTMLButtonElement>(null);

  const dateRange = useMemo(
    () => getDateRangeFromScale(currentDate, viewScale),
    [currentDate, viewScale]
  );

  const rangeLabel = useMemo(() => {
    if (viewScale === '1-day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
    if (viewScale === '3-day' && dateRange.length >= 3) {
      return `${format(dateRange[0], 'MMM d')} - ${format(dateRange[dateRange.length - 1], 'MMM d, yyyy')}`;
    }
    if (viewScale === 'week' && dateRange.length > 0) {
      return `${format(dateRange[0], 'MMM d')} - ${format(dateRange[dateRange.length - 1], 'MMM d, yyyy')}`;
    }
    return '';
  }, [viewScale, currentDate, dateRange]);

  const handlePrevious = () => {
    if (viewScale === '1-day') {
      onDateChange(subDays(currentDate, 1));
    } else if (viewScale === '3-day') {
      onDateChange(subDays(currentDate, 3));
    } else {
      onDateChange(subWeeks(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (viewScale === '1-day') {
      onDateChange(addDays(currentDate, 1));
    } else if (viewScale === '3-day') {
      onDateChange(addDays(currentDate, 3));
    } else {
      onDateChange(addWeeks(currentDate, 1));
    }
  };

  const handlePickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      onDateChange(new Date(value));
      setPickerOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ButtonGroup>
          <Button onClick={handlePrevious} size="small">
            <ChevronLeft />
          </Button>
          <Button
            ref={dateButtonRef}
            onClick={() => setPickerOpen(true)}
            size="small"
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            {rangeLabel}
          </Button>
          <Button onClick={handleNext} size="small">
            <ChevronRight />
          </Button>
          <Tooltip title="Jump to Now">
            <Button onClick={onJumpToNow} size="small" sx={{ minWidth: 32, width: 32, height: 32 }}>
              <PushPinIcon />
            </Button>
          </Tooltip>
        </ButtonGroup>
        <Popover
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          anchorEl={dateButtonRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 2 }}>
            <TextField
              type="date"
              value={format(currentDate, 'yyyy-MM-dd')}
              onChange={handlePickerChange}
              label="Select Date"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Popover>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {!isMobile && (
          <ButtonGroup size="small">
            <Button
              variant={viewScale === '1-day' ? 'contained' : 'outlined'}
              onClick={() => onViewScaleChange('1-day')}
            >
              1 Day
            </Button>
            <Button
              variant={viewScale === '3-day' ? 'contained' : 'outlined'}
              onClick={() => onViewScaleChange('3-day')}
            >
              3 Days
            </Button>
            <Button
              variant={viewScale === 'week' ? 'contained' : 'outlined'}
              onClick={() => onViewScaleChange('week')}
            >
              Week
            </Button>
          </ButtonGroup>
        )}
        {showResolution && onTimeResolutionChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              View:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Resolution</InputLabel>
              <Select
                value={timeResolution}
                label="Resolution"
                onChange={(e) => onTimeResolutionChange(e.target.value as TimeResolution)}
              >
                {TIME_RESOLUTIONS.map((res) => (
                  <MenuItem key={res.value} value={res.value}>
                    {res.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
}
